import { functions } from '../firebase/firebase';
import { httpsCallable } from 'firebase/functions';

// Create callable function references
// Credit report parsing needs longer timeout (5 minutes) for AI processing
const getCreditReportQuotaFunction = httpsCallable(functions, 'getCreditReportQuota');
const parseCreditReportFunction = httpsCallable(functions, 'parseCreditReport', { timeout: 300000 });
const getParsedCreditReportsFunction = httpsCallable(functions, 'getParsedCreditReports');
const getParsedCreditReportByIdFunction = httpsCallable(functions, 'getParsedCreditReportById');
const updateParsedCreditReportFunction = httpsCallable(functions, 'updateParsedCreditReport');

// Utility function to handle Firebase function responses
const handleFirebaseResponse = (result, functionName) => {
  if (!result.data) {
    throw new Error(`No data returned from ${functionName}`);
  }
  if (result.data.success === false && result.data.error) {
    throw new Error(result.data.error);
  }
  return result.data;
};

/**
 * Get the user's credit report parsing quota
 * @param {string|null} clientUserId - Optional client user ID for mentor mode
 * @returns {Promise<Object>} - Quota information including canParse, remainingParses, tier
 */
export const getCreditReportQuota = async (clientUserId = null) => {
  const result = await getCreditReportQuotaFunction({ clientUserId });
  return handleFirebaseResponse(result, 'getCreditReportQuota');
};

/**
 * Parse a credit report file
 * @param {File} file - The file to parse (PDF, DOCX, or DOC)
 * @param {string|null} clientUserId - Optional client user ID for mentor mode
 * @returns {Promise<Object>} - Parse result including reportId, bureau, confidence, summary
 */
export const parseCreditReport = async (file, clientUserId = null) => {
  // Validate file type - only PDF is supported
  const allowedTypes = ['application/pdf'];
  
  if (!allowedTypes.includes(file.type)) {
    throw new Error('Invalid file type. Please upload a PDF file.');
  }

  // Convert file to base64
  const fileContent = await fileToBase64(file);
  
  const result = await parseCreditReportFunction({
    fileContent,
    mimeType: file.type,
    clientUserId
  });
  
  return handleFirebaseResponse(result, 'parseCreditReport');
};

/**
 * Get all parsed credit reports for the user or client
 * @param {Object} options - Query options
 * @param {string|null} options.clientUserId - Client user ID when mentor is viewing client's reports
 * @param {string|null} options.viewMentorId - Mentor ID when client is viewing their mentor's reports
 * @returns {Promise<Object>} - Object containing reports array
 */
export const getParsedCreditReports = async (options = {}) => {
  const { clientUserId = null, viewMentorId = null } = options;
  const result = await getParsedCreditReportsFunction({ clientUserId, viewMentorId });
  return handleFirebaseResponse(result, 'getParsedCreditReports');
};

/**
 * Get a specific parsed credit report by ID
 * @param {string} reportId - The report ID to retrieve
 * @returns {Promise<Object>} - The full report data
 */
export const getParsedCreditReportById = async (reportId) => {
  const result = await getParsedCreditReportByIdFunction({ reportId });
  return handleFirebaseResponse(result, 'getParsedCreditReportById');
};

/**
 * Update a parsed credit report (for user corrections)
 * @param {string} reportId - The report ID to update
 * @param {Object} updates - The updates to apply (reportData)
 * @returns {Promise<Object>} - Success result with updated timestamp
 */
export const updateParsedCreditReport = async (reportId, updates) => {
  const result = await updateParsedCreditReportFunction({ reportId, updates });
  return handleFirebaseResponse(result, 'updateParsedCreditReport');
};

/**
 * Convert a File object to base64 string
 * @param {File} file - The file to convert
 * @returns {Promise<string>} - Base64 encoded file content
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Get file extension display name
 * @param {string} mimeType - The MIME type
 * @returns {string} - Human readable file type
 */
export const getFileTypeDisplay = (mimeType) => {
  const typeMap = {
    'application/pdf': 'PDF',
  };
  return typeMap[mimeType] || 'Unknown';
};

/**
 * Format bureau name for display
 * @param {string} bureau - Bureau identifier (experian, transunion, equifax)
 * @returns {string} - Formatted display name
 */
export const formatBureauName = (bureau) => {
  const bureauMap = {
    'experian': 'Experian',
    'transunion': 'TransUnion',
    'equifax': 'Equifax'
  };
  return bureauMap[bureau?.toLowerCase()] || bureau;
};

/**
 * Get bureau color theme
 * @param {string} bureau - Bureau identifier
 * @returns {Object} - Color theme object { primary, secondary, accent }
 */
export const getBureauTheme = (bureau) => {
  const themes = {
    experian: {
      primary: '#1c2a4a',
      secondary: '#1e88e5',
      accent: '#42a5f5',
      gradient: 'linear-gradient(135deg, #1c2a4a 0%, #1e88e5 100%)'
    },
    transunion: {
      primary: '#0072ce',
      secondary: '#00a3e0',
      accent: '#00c4e0',
      gradient: 'linear-gradient(135deg, #0072ce 0%, #00a3e0 100%)'
    },
    equifax: {
      primary: '#861b4d',
      secondary: '#c41e3a',
      accent: '#e63946',
      gradient: 'linear-gradient(135deg, #861b4d 0%, #c41e3a 100%)'
    }
  };
  return themes[bureau?.toLowerCase()] || themes.experian;
};

/**
 * Safely convert Firestore Timestamp to JavaScript Date
 * Handles various formats: Timestamp objects, seconds property, toMillis(), Date objects, ISO strings, milliseconds
 * @param {any} timestamp - Firestore Timestamp or date representation
 * @returns {Date|null} - JavaScript Date object or null if invalid
 */
export const timestampToDate = (timestamp) => {
  if (!timestamp) return null;
  
  // Already a Date object
  if (timestamp instanceof Date) {
    return isNaN(timestamp.getTime()) ? null : timestamp;
  }
  
  // Firestore Timestamp with toMillis() method
  if (typeof timestamp.toMillis === 'function') {
    return new Date(timestamp.toMillis());
  }
  
  // Object with seconds property (serialized Firestore Timestamp)
  // Handle both 'seconds' and '_seconds' formats
  if (timestamp.seconds != null) {
    return new Date(timestamp.seconds * 1000);
  }
  
  // Handle '_seconds' format (underscore prefix, common in serialized Firestore Timestamps)
  if (timestamp._seconds != null) {
    return new Date(timestamp._seconds * 1000);
  }
  
  // ISO string
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Milliseconds number
  if (typeof timestamp === 'number') {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  }
  
  return null;
};

/**
 * Format a Firestore Timestamp as a localized date string
 * @param {any} timestamp - Firestore Timestamp or date representation
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date string or 'Unknown' if invalid
 */
export const formatTimestamp = (timestamp, options = {}) => {
  const date = timestampToDate(timestamp);
  if (!date) return 'Unknown';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };
  
  return date.toLocaleDateString(undefined, defaultOptions);
};

/**
 * Classify account type into category
 * @param {string} accountType - Account type string
 * @returns {string} - Category: 'revolving', 'mortgage', 'installment', or 'other'
 */
const classifyAccountType = (accountType) => {
  const type = (accountType || '').toLowerCase();
  
  // Revolving accounts (credit cards, lines of credit)
  if (type.includes('credit card') || type.includes('creditcard') || 
      type.includes('revolving') || type.includes('charge card') ||
      type.includes('line of credit') || type.includes('heloc')) {
    return 'revolving';
  }
  
  // Mortgage accounts
  if (type.includes('mortgage') || type.includes('home loan') || 
      type.includes('real estate') || type.includes('home equity')) {
    return 'mortgage';
  }
  
  // Installment loans (auto, student, personal)
  if (type.includes('auto') || type.includes('car') || type.includes('vehicle') ||
      type.includes('student') || type.includes('education') ||
      type.includes('personal') || type.includes('installment') ||
      type.includes('loan') || type.includes('finance')) {
    return 'installment';
  }
  
  return 'other';
};

/**
 * Calculate credit summary from accounts array using business logic
 * This is used as a frontend fallback for reports parsed before backend calculation was added
 * @param {Array} accounts - Array of account objects
 * @returns {Object} - Calculated credit summary
 */
export const calculateCreditSummary = (accounts) => {
  if (!accounts || !Array.isArray(accounts) || accounts.length === 0) {
    return {
      totalAccounts: 0,
      openAccounts: 0,
      closedAccounts: 0,
      delinquentAccounts: 0,
      derogatoryAccounts: 0,
      totalBalances: 0,
      revolvingBalance: 0,
      mortgageBalance: 0,
      installmentBalance: 0,
      otherBalance: 0,
      totalMonthlyPayments: 0,
      oldestAccountDate: '',
      creditUtilization: null,
      totalCreditLimit: 0,
    };
  }

  const totalAccounts = accounts.length;
  let openAccounts = 0;
  let closedAccounts = 0;
  let delinquentAccounts = 0;
  let derogatoryAccounts = 0;
  let totalBalances = 0;
  let revolvingBalance = 0;
  let mortgageBalance = 0;
  let installmentBalance = 0;
  let otherBalance = 0;
  let totalMonthlyPayments = 0;
  let oldestAccountDate = null;
  let totalCreditLimit = 0;

  // Status keywords that indicate negative/derogatory accounts
  const derogatoryKeywords = [
    'collection', 'charge off', 'chargeoff', 'foreclosure',
    'repossession', 'bankruptcy', 'settled', 'written off',
  ];

  // Status keywords that indicate delinquent accounts
  const delinquentKeywords = [
    '30 days late', '60 days late', '90 days late',
    '120 days late', '150 days late', '180 days late',
  ];

  accounts.forEach((account) => {
    const status = (account.accountStatus || '').toLowerCase();
    const statusDetail = (account.accountStatusDetail || '').toLowerCase();
    const paymentStatus = (account.paymentStatus || '').toLowerCase();
    const accountType = (account.accountType || '').toLowerCase();

    // Determine if account is open or closed
    const isClosed = status.includes('closed') ||
        status.includes('paid') ||
        (account.dateClosed != null && account.dateClosed !== '') ||
        statusDetail.includes('closed');

    if (isClosed) {
      closedAccounts++;
    } else {
      openAccounts++;
    }

    // Check for delinquent accounts
    const isDelinquent = delinquentKeywords.some((keyword) =>
      paymentStatus.includes(keyword) ||
        status.includes(keyword) ||
        statusDetail.includes(keyword)
    ) || (account.pastDueAmount != null && account.pastDueAmount > 0);

    if (isDelinquent) {
      delinquentAccounts++;
    }

    // Check for derogatory accounts
    const isDerogatory = derogatoryKeywords.some((keyword) =>
      status.includes(keyword) ||
        statusDetail.includes(keyword) ||
        paymentStatus.includes(keyword)
    );

    if (isDerogatory) {
      derogatoryAccounts++;
    }

    // Sum balances by category
    const balance = account.currentBalance != null ? account.currentBalance : 0;
    totalBalances += balance;
    
    const category = classifyAccountType(accountType);
    switch (category) {
      case 'revolving':
        revolvingBalance += balance;
        break;
      case 'mortgage':
        mortgageBalance += balance;
        break;
      case 'installment':
        installmentBalance += balance;
        break;
      default:
        otherBalance += balance;
    }

    // Sum monthly payments
    const monthlyPayment = account.monthlyPayment != null ? account.monthlyPayment : 0;
    totalMonthlyPayments += monthlyPayment;

    // Track oldest account date
    if (account.dateOpened) {
      const openedDate = new Date(account.dateOpened);
      if (!isNaN(openedDate.getTime())) {
        if (oldestAccountDate === null || openedDate < oldestAccountDate) {
          oldestAccountDate = openedDate;
        }
      }
    }

    // Track credit limit for revolving accounts
    if (category === 'revolving' && account.creditLimit != null && account.creditLimit > 0) {
      totalCreditLimit += account.creditLimit;
    }
  });

  // Calculate credit utilization (only for revolving accounts)
  let creditUtilization = null;
  if (totalCreditLimit > 0) {
    creditUtilization = Math.round((revolvingBalance / totalCreditLimit) * 100 * 100) / 100;
  }

  // Format oldest account date
  let oldestAccountDateStr = '';
  if (oldestAccountDate) {
    const year = oldestAccountDate.getFullYear();
    const month = String(oldestAccountDate.getMonth() + 1).padStart(2, '0');
    const day = String(oldestAccountDate.getDate()).padStart(2, '0');
    oldestAccountDateStr = `${year}-${month}-${day}`;
  }

  return {
    totalAccounts,
    openAccounts,
    closedAccounts,
    delinquentAccounts,
    derogatoryAccounts,
    totalBalances: Math.round(totalBalances * 100) / 100,
    revolvingBalance: Math.round(revolvingBalance * 100) / 100,
    mortgageBalance: Math.round(mortgageBalance * 100) / 100,
    installmentBalance: Math.round(installmentBalance * 100) / 100,
    otherBalance: Math.round(otherBalance * 100) / 100,
    totalMonthlyPayments: Math.round(totalMonthlyPayments * 100) / 100,
    oldestAccountDate: oldestAccountDateStr,
    creditUtilization,
    totalCreditLimit: Math.round(totalCreditLimit * 100) / 100,
  };
};

/**
 * Get effective credit summary - uses calculated values if backend values are missing
 * @param {Object} reportData - The report data from Firestore
 * @returns {Object} - Credit summary with calculated fallbacks
 */
export const getEffectiveCreditSummary = (reportData) => {
  const existingSummary = reportData?.creditSummary || {};
  const accounts = reportData?.accounts || [];
  
  // Check if the summary has valid calculated values
  const hasValidOpenClosed = (existingSummary.openAccounts != null && existingSummary.openAccounts > 0) ||
      (existingSummary.closedAccounts != null && existingSummary.closedAccounts > 0);
  
  // If summary seems incomplete (open + closed = 0 but total > 0), recalculate
  if (!hasValidOpenClosed && accounts.length > 0) {
    const calculated = calculateCreditSummary(accounts);
    return {
      ...existingSummary,
      ...calculated,
    };
  }
  
  return existingSummary;
};

// =============================================================================
// CREDIT CARD IMPORT FROM REPORTS
// =============================================================================

const previewCreditCardImportFunction = httpsCallable(functions, 'previewCreditCardImport');
const importCreditCardsFromReportsFunction = httpsCallable(functions, 'importCreditCardsFromReports');

/**
 * Preview credit cards that would be imported from parsed reports
 * @param {string|null} clientUserId - Client user ID if mentor mode
 * @returns {Promise<Object>} Preview data with cards to import
 */
export const previewCreditCardImport = async (clientUserId = null) => {
  try {
    const result = await previewCreditCardImportFunction({ clientUserId });
    return result.data;
  } catch (error) {
    console.error('Error previewing credit card import:', error);
    throw error;
  }
};

/**
 * Import credit cards from parsed reports to inventory
 * @param {Array} cardsToImport - Array of card objects to import
 * @param {string|null} clientUserId - Client user ID if mentor mode
 * @returns {Promise<Object>} Import result
 */
export const importCreditCardsFromReports = async (cardsToImport, clientUserId = null) => {
  try {
    const result = await importCreditCardsFromReportsFunction({ 
      cardsToImport, 
      clientUserId 
    });
    return result.data;
  } catch (error) {
    console.error('Error importing credit cards from reports:', error);
    throw error;
  }
};

